/**
 * Company Provisioning Service
 * Handles creation of new companies with isolated storage and manager accounts
 */

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  signInWithEmailAndPassword,
  getAuth
} from 'firebase/auth';
import { 
  ref as storageRef,
  uploadString,
  getDownloadURL
} from 'firebase/storage';
import { initializeApp } from 'firebase/app';
import { auth, db, storage } from './client';
import { firebaseConfig } from './config';

// Create a secondary Firebase app instance for user creation
// This prevents signing out the current SuperAdmin user
let secondaryApp: any = null;
let secondaryAuth: any = null;

function getSecondaryAuth() {
  if (!secondaryApp) {
    secondaryApp = initializeApp(firebaseConfig, 'Secondary');
    secondaryAuth = getAuth(secondaryApp);
  }
  return secondaryAuth;
}

/**
 * Generate a URL-friendly slug from company name
 * Examples:
 *   "Ryco Oilfield" → "ryco-oilfield"
 *   "Acme Oil & Gas" → "acme-oil-gas"
 *   "Demo Company 123" → "demo-company-123"
 */
export function generateSlug(companyName: string): string {
  return companyName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')         // Replace spaces with hyphens
    .replace(/-+/g, '-')          // Replace multiple hyphens with single
    .replace(/^-|-$/g, '');       // Remove leading/trailing hyphens
}

/**
 * Get storage bucket path for a company
 */
export function getCompanyStoragePath(companyId: string): string {
  return `companies/${companyId}`;
}

export interface CompanyProvisioningData {
  companyName: string;
  subdomain: string;
  contactEmail: string;
  contactName: string;
  phone?: string;
  adminPassword: string;
  primaryColor?: string;
  secondaryColor?: string;
  wellCount?: number;
  userCount?: number;
  logo?: string; // Base64 or URL
}

export interface ProvisionedCompany {
  companyId: string; // Primary identifier (slug format)
  storagePath: string;
  adminUserId: string;
  companyData: any;
}

/**
 * Provision a new company with:
 * 1. Unique tenant ID
 * 2. Isolated storage path (bucket folder)
 * 3. Manager/admin account
 * 4. Company document in Firestore
 */
export async function provisionNewCompany(
  data: CompanyProvisioningData
): Promise<ProvisionedCompany> {
  
  try {
    // 1. Generate identifiers
    // SCHEMA STANDARD: companyId is the ONLY identifier (slug format)
    const companyId = generateSlug(data.companyName);
    const storagePath = getCompanyStoragePath(companyId);
    
    
    // 2. Check if subdomain/slug already exists
    const existingCompanyRef = doc(db, 'companies', companyId);
    const existingCompany = await getDoc(existingCompanyRef);
    
    if (existingCompany.exists()) {
      throw new Error(`Company with subdomain "${data.subdomain}" already exists`);
    }
    
    // 3. Create manager/admin user in Firebase Auth using SECONDARY auth instance
    // This prevents signing out the current SuperAdmin user
    const secondAuth = getSecondaryAuth();
    const userCredential = await createUserWithEmailAndPassword(
      secondAuth,
      data.contactEmail,
      data.adminPassword
    );
    
    const adminUserId = userCredential.user.uid;
    
    // Update display name
    await updateProfile(userCredential.user, {
      displayName: data.contactName
    });
    
    
    // Sign out the secondary auth immediately
    await signOut(secondAuth);
    
    // 4. Upload company logo to their storage path (if provided)
    let logoUrl = '';
    if (data.logo) {
      try {
        const logoRef = storageRef(storage, `${storagePath}/logo.png`);
        await uploadString(logoRef, data.logo, 'data_url');
        logoUrl = await getDownloadURL(logoRef);
      } catch (error) {
        // Don't fail the entire provisioning if logo upload fails
      }
    }
    
    // 5. Create company document in Firestore
    // NOTE: Only companyId now - companyId removed (Dec 2025)
    const companyData = {
      id: companyId,
      companyId,
      companyName: data.companyName,
      companyDisplayName: data.companyName, // For UI display
      subdomain: data.subdomain,
      contactEmail: data.contactEmail,
      contactName: data.contactName,
      phone: data.phone || '',
      primaryColor: data.primaryColor || '#3B82F6',
      secondaryColor: data.secondaryColor || '#10B981',
      logo: logoUrl,
      storagePath,
      status: 'active',
      wellCount: data.wellCount || 0,
      userCount: data.userCount || 5,
      maxWells: data.wellCount || 100,
      maxUsers: data.userCount || 10,
      createdAt: serverTimestamp(),
      createdBy: 'superadmin',
      subscription: {
        plan: 'standard',
        pricePerWell: 50,
        billingCycle: 'monthly',
        nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      }
    };
    
    await setDoc(doc(db, 'companies', companyId), companyData);
    
    // 6. Create manager user document in Firestore
    // NOTE: Only companyId now - companyId/companyId removed (Dec 2025)
    const managerData = {
      id: adminUserId,
      uid: adminUserId,
      email: data.contactEmail,
      name: data.contactName,
      displayName: data.contactName,
      phone: data.phone || '',
      role: 'manager',
      companyId,
      pin: '',
      rememberDevice: true,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      isActive: true,
      permissions: {
        manageUsers: true,
        manageWells: true,
        viewReports: true,
        manageBilling: true,
      }
    };
    
    // Primary location: tenant-scoped collection
    // Path: companies/{companyId}/users/{userId}
    await setDoc(doc(db, 'companies', companyId, 'users', adminUserId), managerData);
    
    // 7. Initialize company subcollections (wells as subcollection of company)
    // Create a placeholder well in companies/{companyId}/wells subcollection
    await setDoc(doc(db, 'companies', companyId, 'wells', `${companyId}_placeholder`), {
      id: `${companyId}_placeholder`,
      companyId,
      name: 'Placeholder - Delete me',
      wellNumber: '',
      location: '',
      status: 'inactive',
      createdAt: serverTimestamp(),
      isPlaceholder: true,
    });
    
    // NOTE: Production entries are ONLY under wells now
    // Path: companies/{companyId}/wells/{wellId}/production_entries/{dateId}
    // No flat /production collection needed
    
    
    // 8. Create storage structure (create a .keep file)
    try {
      const keepRef = storageRef(storage, `${storagePath}/.keep`);
      await uploadString(keepRef, 'Company storage initialized', 'raw');
    } catch (storageError) {
      // Storage structure will be created automatically when first file is uploaded
      // This is not critical - we can continue
    }
    
    
    return {
      companyId, // Primary identifier
      storagePath,
      adminUserId,
      companyData,
    };
    
  } catch (error: any) {
    console.error('❌ Company provisioning failed:', error);
    throw new Error(`Failed to provision company: ${error.message}`);
  }
}

/**
 * Get company storage reference
 */
export function getCompanyStorageRef(companyId: string, path: string = '') {
  const basePath = getCompanyStoragePath(companyId);
  return storageRef(storage, path ? `${basePath}/${path}` : basePath);
}

/**
 * Check if a subdomain is available
 */
export async function checkSubdomainAvailability(subdomain: string): Promise<boolean> {
  const slug = generateSlug(subdomain);
  const companyRef = doc(db, 'companies', slug);
  const companySnap = await getDoc(companyRef);
  return !companySnap.exists();
}
